import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";

export const Navbar = ({ theme = "dark", links, ctaLabel, ctaHref, logoSuffix, logoTo, backLink }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const isOcean = theme === "ocean";
  const activeClass = isOcean ? "bg-white text-ocean-deep" : "bg-brand-cyan text-ink";

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 backdrop-blur-xl border-b ${isOcean ? "bg-ocean-deep/60 border-white/10" : "bg-ink/60 border-white/10"}`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 lg:px-20 h-20">
        <Logo suffix={logoSuffix} to={logoTo} />

        <div className="hidden md:flex items-center gap-9">
          {backLink && (
            <Link
              to={backLink.to}
              data-testid="nav-back-link"
              className="flex items-center gap-1.5 font-manrope text-sm text-white/50 hover:text-brand-cyan transition-colors duration-300"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> {backLink.label}
            </Link>
          )}
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              data-testid={`nav-link-${link.href.replace("#", "")}`}
              className="relative font-manrope text-sm text-white/70 hover:text-white transition-colors duration-300 group"
            >
              {link.label}
              <span
                className={`absolute -bottom-1.5 left-0 w-0 h-px group-hover:w-full transition-all duration-300 ${isOcean ? "bg-cyan-300" : "bg-brand-cyan"}`}
              />
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-5">
          <div className="flex items-center border border-white/15 rounded-full overflow-hidden" data-testid="language-switcher">
            <button
              onClick={() => setLang("en")}
              data-testid="language-toggle-en"
              className={`font-mono text-xs tracking-widest px-3 py-1.5 transition-colors duration-300 ${lang === "en" ? activeClass : "text-white/50 hover:text-white"}`}
            >
              EN
            </button>
            <button
              onClick={() => setLang("pt")}
              data-testid="language-toggle-pt"
              className={`font-mono text-xs tracking-widest px-3 py-1.5 transition-colors duration-300 ${lang === "pt" ? activeClass : "text-white/50 hover:text-white"}`}
            >
              PT
            </button>
          </div>
          <a
            href={ctaHref}
            data-testid="nav-cta-button"
            className={`font-manrope text-sm font-semibold px-5 py-2.5 transition-colors duration-300 ${isOcean ? "bg-white text-ocean-deep rounded-full hover:bg-cyan-200" : "bg-brand-cyan text-ink hover:bg-white"}`}
          >
            {ctaLabel}
          </a>
        </div>

        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`md:hidden overflow-hidden border-t ${isOcean ? "border-white/10 bg-ocean-deep/95" : "border-white/10 bg-ink/95"}`}
            data-testid="mobile-menu-panel"
          >
            <div className="flex flex-col gap-5 px-6 py-8">
              {backLink && (
                <Link to={backLink.to} onClick={() => setOpen(false)} data-testid="nav-back-link-mobile" className="font-manrope text-white/60">
                  {backLink.label}
                </Link>
              )}
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-mobile-${link.href.replace("#", "")}`}
                  className="font-manrope text-white"
                >
                  {link.label}
                </a>
              ))}
              <div className="flex items-center border border-white/15 rounded-full overflow-hidden w-fit" data-testid="language-switcher-mobile">
                <button
                  onClick={() => setLang("en")}
                  data-testid="language-toggle-en-mobile"
                  className={`font-mono text-xs tracking-widest px-3 py-1.5 transition-colors duration-300 ${lang === "en" ? activeClass : "text-white/50"}`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("pt")}
                  data-testid="language-toggle-pt-mobile"
                  className={`font-mono text-xs tracking-widest px-3 py-1.5 transition-colors duration-300 ${lang === "pt" ? activeClass : "text-white/50"}`}
                >
                  PT
                </button>
              </div>
              <a
                href={ctaHref}
                onClick={() => setOpen(false)}
                data-testid="nav-cta-button-mobile"
                className={`font-manrope text-sm font-semibold px-5 py-2.5 w-fit ${isOcean ? "bg-white text-ocean-deep rounded-full" : "bg-brand-cyan text-ink"}`}
              >
                {ctaLabel}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
